import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook for formatting timestamps in a human-readable relative format.
 * Past and future: just now, minutes/hours/days/months/years ago or from now.
 */
export default function useFormatTimestamp() {
  const { t } = useTranslation();

  const formatTimestamp = useCallback(
    (timestamp: string | Date): string => {
      const now = new Date();
      const targetTime = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
      const signedMinutes = Math.floor((now.getTime() - targetTime.getTime()) / (1000 * 60));
      const absMinutes = Math.abs(signedMinutes);
      const isFuture = signedMinutes < 0;

      if (absMinutes < 1) {
        return t('common.justNow');
      }

      const key = (past: string, future: string) => (isFuture ? future : past);

      if (absMinutes < 60) {
        return t(key('common.minutesAgo', 'common.inMinutes'), { count: absMinutes });
      }
      if (absMinutes < 1440) {
        return t(key('common.hoursAgo', 'common.inHours'), {
          count: Math.floor(absMinutes / 60),
        });
      }
      if (absMinutes < 43200) {
        return t(key('common.daysAgo', 'common.inDays'), {
          count: Math.floor(absMinutes / 1440),
        });
      }
      if (absMinutes < 525600) {
        return t(key('common.monthsAgo', 'common.inMonths'), {
          count: Math.floor(absMinutes / 43200),
        });
      }
      return t(key('common.yearsAgo', 'common.inYears'), {
        count: Math.floor(absMinutes / 525600),
      });
    },
    [t],
  );

  return formatTimestamp;
}
